import type { BetSlipResponse } from '@mobi/betslip/types'
import { trackKey } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { UpdateAccountBalance } from '@core/State/UserAccount/userAccountDriver'
import { fetchCampaignsAsync } from '@core/State/UserAccount/async-signals'
import { isBetslipResponses } from '@core/Data/betslip'
import {
  BetRequest,
  proposeBet,
  confirmBet,
  FobSingleErrorResponse,
  isMysteryCommitResponse,
  MysteryCommitResponse,
  ToteBetResponse,
  FobCommitResponse,
} from '@core/Data/betting'
import {
  ConfirmBetFailed,
  ProposeBetFailed,
  ConfirmBetslipBetsFailed,
  ConfirmBetslipBetsSucceeded,
  ProposeBetslipBetsSucceeded,
  ProposeBetSucceeded,
  ProposeBet,
  ConfirmBet,
  ConfirmBetSucceeded,
  SetBonusBet,
  ToggleBonusBetUsage,
} from '@core/Areas/Quickbet/signals'
import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'

export async function proposeBetAsync(request: BetRequest): Promise<number | undefined> {
  ProposeBet()
  try {
    const response = await proposeBet(request)
    if (!response) return undefined

    if (isBetslipResponses(response)) return handleBetslipProposeResponses(response)

    response.accountBalance && UpdateAccountBalance({ balance: response.accountBalance })
    ProposeBetSucceeded(response)

    return response.accountBalance
  } catch (error) {
    ProposeBetFailed(error as FobSingleErrorResponse)
    throw error
  }
}

export async function confirmBetAsync(request: BetRequest): Promise<void> {
  ConfirmBet()
  try {
    const response = await confirmBet(request)
    if (response) {
      isBetslipResponses(response)
        ? handleBetslipCommitResponses(response)
        : handleQuickbetResponse(response)
    }
  } catch (err) {
    const error = err as FobSingleErrorResponse
    if (error.response && error.response.accountBalance) {
      UpdateAccountBalance({ balance: error.response.accountBalance })
    }
    ConfirmBetFailed(error)
  }
}

// =============
// Local Helpers
// =============

function handleBetslipProposeResponses(response: BetSlipResponse[]) {
  const accountBalance = response[0]?.accountBalance
  accountBalance && UpdateAccountBalance({ balance: accountBalance })

  ProposeBetslipBetsSucceeded()
  return accountBalance
}

function handleBetslipCommitResponses(response: BetSlipResponse[]) {
  const successfulResponses = response.filter(r => r.success)

  // all failed
  if (successfulResponses.length === 0) {
    const errorResponses = response.flatMap(r => (r.error ? [r.error] : []))
    ConfirmBetslipBetsFailed(errorResponses)
    return
  }

  const successfulResponsesByDateDescending = successfulResponses.sort(
    (a, b) =>
      new Date(b.receipt?.betPlacedTime ?? 0).getTime() -
      new Date(a.receipt?.betPlacedTime ?? 0).getTime()
  )

  const lastReceipt = successfulResponsesByDateDescending[0].receipt
  if (lastReceipt) {
    UpdateAccountBalance({
      balance: lastReceipt.accountBalance,
    })
    if (lastReceipt.campaignActivatedInd) {
      fetchCampaignsAsync()
      trackKey(keys.redeemedBonusOffer)
    }
  }

  ConfirmBetslipBetsSucceeded(response)
}

function handleQuickbetResponse(
  response: ToteBetResponse | MysteryCommitResponse | FobCommitResponse
) {
  if (response.accountBalance !== undefined) {
    UpdateAccountBalance({ balance: response.accountBalance })
  }

  if (isMysteryCommitResponse(response)) {
    response = buildMysteryConfirmBetSucceededResponse(response as MysteryCommitResponse)
  }

  ConfirmBetSucceeded(response)
  if (response.campaignActivatedInd) {
    fetchCampaignsAsync()
    trackKey(keys.redeemedBonusOffer)
  }

  SetBonusBet(null)
  ToggleBonusBetUsage(false)
}

function buildMysteryConfirmBetSucceededResponse(
  mysteryResponse: MysteryCommitResponse
): ToteBetResponse {
  const bet = mysteryResponse.bets[0]
  bet.accountBalance = mysteryResponse.accountBalance
  bet.campaignActivatedInd = mysteryResponse.campaignActivatedInd

  return bet
}
